/**
 * @lodex-pro/web-shared/auth/password-field — masked input with a show/hide toggle.
 *
 * Quick 260827-pwd. Every password field in the hauler web apps was a bare
 * `<input type="password">`, so a user could only trust the dots and hope. Mobile
 * has had `components/PasswordInput.tsx` with an eye toggle since 260619; this is
 * the web counterpart and deliberately mirrors its contract — a button whose
 * accessible name flips between "Show password" and "Hide password", and a type
 * swap between `password` and `text`.
 *
 * Mobile draws its eye from Views because bare Expo has no icon library. The web
 * packages all depend on lucide-react and web-shared already renders lucide icons
 * in components/ui, so this uses Eye / EyeOff rather than reinventing a glyph.
 *
 * CONTRACT: like every other component in this directory, the markup is class-less
 * semantic HTML — the consuming app's stylesheet (.auth-card and friends) skins it.
 * The one exception is the wrapper's `position: relative` and the button's absolute
 * placement, which are inline because the toggle is meaningless if it does not sit
 * on the field, and requiring four stylesheets to agree on that would be fragile.
 */

import { useId, useState, forwardRef } from 'react';
import type { ComponentPropsWithoutRef } from 'react';
import { Eye, EyeOff } from 'lucide-react';

export interface PasswordFieldProps extends Omit<ComponentPropsWithoutRef<'input'>, 'type'> {
  /** Visible label text. */
  readonly label: string;
  /** Validation message rendered in a role="alert" below the field. */
  readonly error?: string | undefined;
  /** Optional helper text (e.g. the complexity rules). */
  readonly hint?: string | undefined;
}

export const PasswordField = forwardRef<HTMLInputElement, PasswordFieldProps>(
  function PasswordField({ label, error, hint, id, className, ...inputProps }, ref) {
    const generatedId = useId();
    const fieldId = id ?? generatedId;
    const hintId = `${fieldId}-hint`;
    const [revealed, setRevealed] = useState(false);

    return (
      <>
        <label htmlFor={fieldId}>{label}</label>
        <span style={{ position: 'relative', display: 'block' }}>
          <input
            {...inputProps}
            id={fieldId}
            className={className !== undefined ? `keep-case ${className}` : 'keep-case'}
            ref={ref}
            type={revealed ? 'text' : 'password'}
            aria-describedby={hint !== undefined ? hintId : undefined}
            aria-invalid={error !== undefined ? true : undefined}
          />
          <button
            type="button"
            onClick={() => setRevealed((r) => !r)}
            aria-label={revealed ? 'Hide password' : 'Show password'}
            aria-pressed={revealed}
            // Not a form control — never let it steal the Enter key or tab focus
            // ahead of the submit button.
            tabIndex={-1}
            // 2026-10-21: a 56px tap target over the right end of the field (it overhangs the
            // field's height; it is transparent). design/auth.css sizes the input's right
            // padding to clear it and colours it.
            className="password-toggle"
            style={{
              position: 'absolute',
              right: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              width: '56px',
              height: '56px',
              margin: 0,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
            }}
          >
            {revealed ? (
              <EyeOff size={18} aria-hidden="true" />
            ) : (
              <Eye size={18} aria-hidden="true" />
            )}
          </button>
        </span>
        {hint !== undefined && <small id={hintId}>{hint}</small>}
        {error !== undefined && <p role="alert">{error}</p>}
      </>
    );
  },
);
